import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Heart, Check, Loader2, Ticket, Gift, X } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { usePartnerBranding } from "@/contexts/PartnerBrandingContext";
import { PartnerContextBanner } from "@/components/partner/PartnerBrandedWrapper";

const passwordRequirements = [
  { label: "At least 8 characters", test: (p: string) => p.length >= 8 },
  { label: "One uppercase letter", test: (p: string) => /[A-Z]/.test(p) },
  { label: "One lowercase letter", test: (p: string) => /[a-z]/.test(p) },
  { label: "One number", test: (p: string) => /[0-9]/.test(p) },
];

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signUp, signInWithGoogle, signInWithApple, user, loading: authLoading } = useAuth();
  const { partner, isPartnerContext } = usePartnerBranding();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [checkingSettings, setCheckingSettings] = useState(true);
  const [requireInvitation, setRequireInvitation] = useState(true);
  const [inviteCode, setInviteCode] = useState(searchParams.get('code') || '');
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') || '');
  const isGiftClaim = searchParams.get('gift') === 'true';
  const giftEmail = searchParams.get('email') || '';
  const [inviteValid, setInviteValid] = useState(false);
  const [validatingCode, setValidatingCode] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: giftEmail,
    password: "",
    confirmPassword: "",
    purpose: "memorial"
  });
  
  // Build partner query string for navigation
  const partnerParam = searchParams.get('partner') ? `?partner=${searchParams.get('partner')}` : '';

  // Check registration settings on mount
  useEffect(() => {
    const checkSettings = async () => {
      try {
        const { data } = await supabase.functions.invoke('manage-invitations', {
          body: { action: 'check_settings' }
        });
        setRequireInvitation(data?.require_invitation ?? true);
        
        // If code is in URL, validate it
        if (searchParams.get('code')) {
          validateInviteCode(searchParams.get('code')!);
        }
      } catch (error) {
        console.error('Error checking settings:', error);
      }
      setCheckingSettings(false);
    };
    checkSettings();
  }, [searchParams]);

  const validateInviteCode = async (code: string) => {
    if (!code || code.length < 6) {
      setInviteValid(false);
      return;
    }
    
    setValidatingCode(true);
    try {
      const { data } = await supabase.functions.invoke('manage-invitations', {
        body: { action: 'validate', code }
      });
      
      if (data?.valid) {
        setInviteValid(true);
        if (data.email && !formData.email) {
          setFormData(prev => ({ ...prev, email: data.email }));
        }
        toast({
          title: "Valid invitation",
          description: "Your invitation code has been verified."
        });
      } else {
        setInviteValid(false);
        toast({
          title: "Invalid code",
          description: data?.error || "This invitation code is invalid or expired.",
          variant: "destructive"
        });
      }
    } catch (error) {
      setInviteValid(false);
    }
    setValidatingCode(false);
  };

  useEffect(() => {
    if (user && !authLoading) {
      navigate("/dashboard");
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check invitation requirement
    if (requireInvitation && !inviteValid) {
      toast({
        title: "Invitation required",
        description: "Please enter a valid invitation code to register.",
        variant: "destructive"
      });
      return;
    }

    if (!formData.name || !formData.email || !formData.password) {
      toast({
        title: "Missing fields",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    const allRequirementsMet = passwordRequirements.every(req => req.test(formData.password));
    if (!allRequirementsMet) {
      toast({
        title: "Password requirements not met",
        description: "Please ensure your password meets all requirements.",
        variant: "destructive"
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure your passwords match.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    const { error } = await signUp(formData.email, formData.password, {
      full_name: formData.name,
      purpose: formData.purpose
    });

    if (error) {
      setLoading(false);
      if (error.message.includes("already registered")) {
        toast({
          title: "Account exists",
          description: "An account with this email already exists. Please sign in instead.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Sign up failed",
          description: error.message,
          variant: "destructive"
        });
      }
      return;
    }

    // Mark invitation code as used (code will be marked when user first signs in)
    if (inviteCode && inviteValid) {
      // Store the code in localStorage to mark as used after auth completes
      localStorage.setItem('pending_invite_code', inviteCode);
    }

    // Store referral code if present
    if (referralCode) {
      localStorage.setItem('pending_referral_code', referralCode);
    }

    // Store gift claim flag if present
    if (isGiftClaim) {
      localStorage.setItem('pending_gift_claim', 'true');
    }

    setLoading(false);
    toast({
      title: "Account created!",
      description: "Welcome to Living Tribute."
    });
    navigate("/onboarding");
  };

  // Check if social login is allowed (only when invitation not required OR valid code entered)
  const socialLoginEnabled = !requireInvitation || inviteValid;

  const handleGoogleSignIn = async () => {
    setLoading(true);
    const { error } = await signInWithGoogle();
    if (error) {
      setLoading(false);
      toast({
        title: "Google sign in failed",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleAppleSignIn = async () => {
    setLoading(true);
    const { error } = await signInWithApple();
    if (error) {
      setLoading(false);
      toast({
        title: "Apple sign in failed",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  if (authLoading || checkingSettings) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <Heart className="w-8 h-8 text-accent fill-accent" />
            <span className="text-2xl font-semibold text-foreground">Living Tribute</span>
          </Link>
        </div>

        <Card className="card-soft border-0">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl">Create your account</CardTitle>
            <CardDescription>Get started for free — no credit card required</CardDescription>
            {referralCode && (
              <div className="flex items-center justify-center gap-2 mt-2 text-sm text-accent">
                <Gift className="w-4 h-4" />
                <span>You were referred by a friend!</span>
              </div>
            )}
            {isGiftClaim && (
              <div className="flex items-center justify-center gap-2 mt-2 text-sm text-accent">
                <Gift className="w-4 h-4" />
                <span>Create your account to claim your Premium Tribute gift!</span>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            <PartnerContextBanner />
            
            {/* Invitation Code Section */}
            {requireInvitation && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Ticket className="w-4 h-4 text-accent" />
                  <Label htmlFor="inviteCode" className="font-medium">Invitation Code</Label>
                </div>
                <div className="flex gap-2">
                  <Input
                    id="inviteCode"
                    placeholder="Enter your code"
                    value={inviteCode}
                    onChange={(e) => {
                      setInviteCode(e.target.value.toUpperCase());
                      setInviteValid(false);
                    }}
                    className="h-12 rounded-xl font-mono tracking-widest uppercase"
                    disabled={loading || validatingCode || inviteValid}
                    maxLength={8}
                  />
                  <Button
                    type="button"
                    onClick={() => validateInviteCode(inviteCode)}
                    disabled={loading || validatingCode || inviteCode.length < 6 || inviteValid}
                    className="h-12 px-6"
                  >
                    {validatingCode ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : inviteValid ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      "Verify"
                    )}
                  </Button>
                </div>
                {inviteValid && (
                  <p className="text-sm text-green-600 flex items-center gap-1">
                    <Check className="w-4 h-4" />
                    Invitation verified! You can now create your account.
                  </p>
                )}
                {!inviteValid && inviteCode.length >= 6 && !validatingCode && (
                  <p className="text-xs text-muted-foreground">
                    Don't have a code? Contact us to request an invitation.
                  </p>
                )}
              </div>
            )}

            {/* Show rest of form only when invitation is valid (or not required) */}
            {(!requireInvitation || inviteValid) && (
              <>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="h-12 rounded-xl"
                      disabled={loading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="h-12 rounded-xl"
                      disabled={loading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="h-12 rounded-xl"
                      disabled={loading}
                    />
                    {formData.password && (
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {passwordRequirements.map((req) => {
                          const met = req.test(formData.password);
                          return (
                            <div
                              key={req.label}
                              className={`flex items-center gap-1.5 text-xs ${
                                met ? "text-green-600" : "text-muted-foreground"
                              }`}
                            >
                              {met ? (
                                <Check className="w-3 h-3" />
                              ) : (
                                <X className="w-3 h-3" />
                              )}
                              <span>{req.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className="h-12 rounded-xl"
                      disabled={loading}
                    />
                    {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                      <p className="text-xs text-destructive flex items-center gap-1">
                        <X className="w-3 h-3" />
                        Passwords do not match
                      </p>
                    )}
                  </div>

                  <div className="space-y-3">
                    <Label>I'm creating a tribute...</Label>
                    <RadioGroup
                      value={formData.purpose}
                      onValueChange={(value) => setFormData({ ...formData, purpose: value })}
                      className="space-y-2"
                      disabled={loading}
                    >
                      <div className="flex items-center space-x-3 p-4 rounded-xl border border-border hover:border-accent transition-colors cursor-pointer">
                        <RadioGroupItem value="memorial" id="memorial" />
                        <Label htmlFor="memorial" className="cursor-pointer flex-1">
                          <span className="font-medium">For someone who has passed</span>
                          <p className="text-sm text-muted-foreground">Honor their memory with family and friends</p>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-3 p-4 rounded-xl border border-border hover:border-accent transition-colors cursor-pointer">
                        <RadioGroupItem value="planning" id="planning" />
                        <Label htmlFor="planning" className="cursor-pointer flex-1">
                          <span className="font-medium">Planning ahead for myself</span>
                          <p className="text-sm text-muted-foreground">Create your own tribute in advance</p>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 rounded-xl bg-primary hover:bg-primary/90"
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create Account"}
                  </Button>
                </form>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
                  </div>
                </div>

                <Button 
                  variant="outline" 
                  className="w-full h-12 rounded-xl"
                  onClick={handleGoogleSignIn}
                  disabled={loading || !socialLoginEnabled}
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google
                </Button>
              </>
            )}

            <p className="text-center text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link to="/login" className="text-accent hover:underline font-medium">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>

        <div className="flex justify-center gap-6 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Check className="w-4 h-4 text-accent" />
            <span>Free to start</span>
          </div>
          <div className="flex items-center gap-1">
            <Check className="w-4 h-4 text-accent" />
            <span>No credit card</span>
          </div>
        </div>
      </div>
    </div>
  );
}
